import type { PointOptionsObject } from "highcharts";
import * as io from "io-ts";
import React from "react";
import t from "../../../../../../i18n";
import { colors } from "@ui/Theme";
import validateInterface from "@ui/lib/validateInterface";
import type { Options } from "@ui/Highcharts";
import { useQOI } from "../../../useQOI";
import { useReportingTooltipRender } from "../../../useReportingTooltipRender";
import { useProjectAggregationTargets } from "../useProjectAggregation";
import { useOrdering } from "./useOrdering";
import { useTranslations } from "../../../../../../api/useTranslations";
import type { PerformanceReporting } from "../../../../../../api/useReporting";

interface OptionParamerters {
  reporting: PerformanceReporting;
  series: Highcharts.SeriesColumnOptions[];
}

export const FleetRelativeGapsPointMetadata = io.strict({
  measured: io.number,
  expected: io.number,
  gap: io.number,
});

export function useOptions({ reporting, series }: OptionParamerters) {
  const translations = useTranslations();
  const aggregationTargets = useProjectAggregationTargets(reporting);
  const renderTooltip = useReportingTooltipRender();
  const [qoi] = useQOI(reporting);
  const [selectedOrdering] = useOrdering({});

  const constantTooltipFormatter = React.useCallback(
    function (this: Highcharts.TooltipFormatterContextObject) {
      const points = this.points ?? [];

      if (points.length === 0) return;

      const serieValues = points.map(p => {
        const { expected, gap } = validateInterface(FleetRelativeGapsPointMetadata, p.point.custom);

        return [
          {
            name: p.series.name,
            color: p.color as string,
            value: p.y,
            unit: "%",
          },
          {
            name: t("reporting.fleet_internal_gaps.chart.mean_internal_gap", translations),
            color: "",
            value: gap,
            unit: qoi?.unit ?? "",
          },
          {
            name: t("reporting.fleet_internal_gaps.chart.mean_expected"),
            color: "",
            value: expected,
            unit: qoi?.unit ?? "",
          },
        ];
      });

      const [previousPointValue = null, currentPointValue = null] = serieValues.map(
        values => values[0]?.value,
      );
      const valuesEvolution =
        currentPointValue && previousPointValue ? currentPointValue - previousPointValue : null;

      return renderTooltip({
        title: points[0]?.x ? String(points[0].x) : "",
        values: [
          ...serieValues,
          ...(valuesEvolution
            ? [
                [
                  {
                    name: t("reporting.fleet_internal_gaps.chart.tootip.evolution"),
                    color: "",
                    value: valuesEvolution,
                    unit: "p.p",
                  },
                ],
              ]
            : []),
        ],
      });
    },
    [renderTooltip, qoi?.unit, translations],
  );

  const xAxisCategories = React.useMemo(
    () =>
      aggregationTargets
        .map(({ name }) => name)
        .sort((a, b) => {
          if (selectedOrdering === "FAILURE_ORDERING") {
            const currentPeriodSerie = series.find(({ custom }) => custom?.["current"] === true);
            const data = (currentPeriodSerie?.data ?? []) as PointOptionsObject[];
            const aPoint = data.find(p => p.name === a);
            const bPoint = data.find(p => p.name === b);
            return (bPoint?.y ?? 0) - (aPoint?.y ?? 0);
          }

          if (selectedOrdering === "ALPHABETICAL_ORDERING") {
            return a.localeCompare(b);
          }
          return 0;
        }),
    [aggregationTargets, selectedOrdering, series],
  );

  const nonEmptyXAxisCategories = React.useMemo(
    () =>
      xAxisCategories.filter(c =>
        series
          .filter(s => s.visible)
          .some(s => s.data?.some(d => (d as PointOptionsObject).name === c)),
      ),
    [xAxisCategories, series],
  );

  return React.useMemo(
    (): Options => ({
      initial: {},
      chart: {
        ignoreHiddenSeries: true,
        zoomType: "x",
        marginTop: 10,
      },
      tooltip: {
        borderColor: colors.raw.titanium500,
        shared: true,
        formatter: constantTooltipFormatter,
      },
      series,
      xAxis: {
        id: "categoryAxis",
        type: "category",
        categories: nonEmptyXAxisCategories,
      },
      yAxis: {
        id: "performance-internal-losses-comparison",
        title: {
          textAlign: "center",
          useHTML: true,
          text: t("reporting.fleet_internal_gaps.chart.relative_gaps", translations),
        },
        labels: {
          unit: "%",
          sign: false,
          useHTML: true,
        },
      },
      navigator: {
        enabled: false,
      },
    }),
    [constantTooltipFormatter, nonEmptyXAxisCategories, series, translations],
  );
}
